import * as z from 'zod';

const eventAlertsBasePath = 'event-alerts/';
const eventAlertsV2BasePath = 'event-alerts/v2/';

const subscriptionSourceTypeSchema = z.enum(['scenario-insight', 'alfred']);
export type SourceType = z.infer<typeof subscriptionSourceTypeSchema>;

const alertSourceTypeSchema = z.enum([
  ...subscriptionSourceTypeSchema.options,
  'kg-insight',
]);
export type AlertSourceType = z.infer<typeof alertSourceTypeSchema>;

export const kgInsightSubscriptionCategorySchema = z.enum([
  'earnings_preview',
  'earnings_recap',
]);
export type KgInsightSubscriptionCategory = z.infer<
  typeof kgInsightSubscriptionCategorySchema
>;

const trackingStatusZodEnum = z.enum(['active', 'stopped', 'completed']);
export type TrackingStatus = z.infer<typeof trackingStatusZodEnum>;

export const postSubscriptionRequestSchema = z.object({
  source_id: z.string(),
  source_type: subscriptionSourceTypeSchema,
  track: z.boolean(),
});
export type PostSubscriptionRequest = z.infer<
  typeof postSubscriptionRequestSchema
>;

export const getSingleSubscriptionResponseSchema = z.object({
  source_id: z.string(),
  source_type: subscriptionSourceTypeSchema,
  tracking_status: trackingStatusZodEnum,
});
export type GetSingleSubscriptionResponse = z.infer<
  typeof getSingleSubscriptionResponseSchema
>;

export const postKgInsightSubscriptionRequestSchema = z.object({
  entity_tag: z.string(),
  source_type: z.literal('kg-insight'),
  category: kgInsightSubscriptionCategorySchema.optional(),
  track: z.boolean(),
});
export type PostKgInsightSubscriptionRequest = z.infer<
  typeof postKgInsightSubscriptionRequestSchema
>;

export const getKgInsightSubscriptionResponseSchema = z.object({
  entity_tag: z.string(),
  source_type: z.literal('kg-insight'),
  category: kgInsightSubscriptionCategorySchema.optional(),
  tracking_status: trackingStatusZodEnum,
});
export type GetKgInsightSubscriptionResponse = z.infer<
  typeof getKgInsightSubscriptionResponseSchema
>;

export const getSubscriptionsResponseSchema = z.object({
  subscriptions: z.array(z.string()).optional(),
  next_cursor: z.string().optional(),
  prev_cursor: z.string().optional(),
});

export type GetSubscriptionsResponse = z.infer<
  typeof getSubscriptionsResponseSchema
>;

export const getSubscriptionsQuerySchema = z.object({
  source_type: subscriptionSourceTypeSchema,
});
export type GetSubscriptionsQuery = z.infer<typeof getSubscriptionsQuerySchema>;

export const postReadRequestSchema = z.object({
  ids: z.array(z.string()),
});
export type PostReadRequest = z.infer<typeof postReadRequestSchema>;

const alfredAlertPayloadSchema = z.object({
  request_id: z.string(),
  status: z.enum(['success', 'error']),
  error: z.string().optional(),
});

const kgInsightAlertPayloadSchema = z.object({
  id: z.string(),
  category: kgInsightSubscriptionCategorySchema,
  title: z.string(),
});

const alertMetadataSchema = z.object({
  title: z.string().optional(),
  category: kgInsightSubscriptionCategorySchema.optional(),
  tracking_end_datetime: z.string().optional(),
  entity_tag: z.string().optional(),
  tracking_days_remaining: z.number().int().optional(),
  tracking_days_elapsed: z.number().int().optional(),
  tracking_condition: z.string().optional(),
  payload: z
    .union([alfredAlertPayloadSchema, kgInsightAlertPayloadSchema])
    .optional(),
});

const alertSchema = z.object({
  id: z.string(),
  source_id: z.string(),
  source_type: alertSourceTypeSchema,
  title: z.string().optional(),
  created_at: z.string(),
  read: z.boolean(),
  tracking_status: trackingStatusZodEnum.optional(),
  metadata: alertMetadataSchema.optional(),
});
export type Alert = z.infer<typeof alertSchema>;

export const postAlertsResponseSchema = z.object({
  alerts: z.array(alertSchema).nullish(),
  next_cursor: z.string().optional(),
  count: z.number().int().optional(),
});
export type PostAlertsResponse = z.infer<typeof postAlertsResponseSchema>;

export const postAlertsRequestSchema = z.object({
  type: alertSourceTypeSchema.optional(),
  read: z.boolean().optional(),
  limit: z.number().int().optional(),
  next: z.string().optional(),
  count_only: z.boolean().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
});
export type PostAlertsRequest = z.infer<typeof postAlertsRequestSchema>;

export const postAlertsV2RequestSchema = postAlertsRequestSchema
  .omit({ type: true })
  .extend({
    types: z.array(alertSourceTypeSchema).optional(),
    prev: z.string().optional(),
  });
export type PostAlertsV2Request = z.infer<typeof postAlertsV2RequestSchema>;

export const eventAlerts = {
  postSubscriptions: {
    path: `${eventAlertsBasePath}subscriptions`,
  },
  postKgInsightSubscription: {
    path: `${eventAlertsV2BasePath}subscriptions`,
  },
  getKgInsightSubscription: {
    path: (entityTag: string) =>
      `${eventAlertsV2BasePath}subscriptions/${encodeURIComponent(entityTag)}?source_type=kg-insight`,
    schema: getKgInsightSubscriptionResponseSchema,
  },
  getSingleSubscription: {
    path: ({
      sourceType,
      sourceId,
    }: {
      sourceType: SourceType;
      sourceId: string;
    }) => {
      return `${eventAlertsBasePath}subscriptions${`/${sourceId}`}?source_type=${sourceType}`;
    },
    schema: getSingleSubscriptionResponseSchema,
  },
  getSubscriptions: {
    path: ({
      sourceType,
      next,
      limit,
    }: {
      sourceType: SourceType;
      next?: string | null;
      limit?: number;
    }) => {
      const params = new URLSearchParams(
        Object.entries({ source_type: sourceType, next, limit }).filter(
          (entry): entry is [string, string] => entry[1] !== undefined
        )
      );
      return `${eventAlertsBasePath}subscriptions?${params.toString()}`;
    },
    schema: getSubscriptionsResponseSchema,
  },
  postRead: {
    path: `${eventAlertsBasePath}read`,
  },
  postReadAll: {
    path: `${eventAlertsBasePath}read-all`,
  },
  postAlerts: {
    path: `${eventAlertsBasePath}alerts`,
    schema: postAlertsResponseSchema,
  },
  postAlertsV2: {
    path: `${eventAlertsV2BasePath}alerts`,
    schema: postAlertsResponseSchema,
  },
};

export interface AlfredResponsePayload {
  title: string;
  body: string;
  metadata: {
    conversation_id: string;
    request_id: string;
    record_id?: string;
  };
  event: {
    type: 'conversation';
    action:
      | 'request.update'
      | 'request.success'
      | 'request.fail'
      | 'request.cancel';
  };
}

interface InsightAlertMetadata {
  title: string;
  tracking_end_datetime: string;
  entity_tag: string;
  tracking_days_remaining?: number;
  tracking_days_elapsed?: number;
}

interface AlfredAlertMetadata {
  payload: z.infer<typeof alfredAlertPayloadSchema>;
}

interface KgInsightAlertMetadata {
  title: string;
  entity_tag: string;
  category: KgInsightSubscriptionCategory;
  payload: z.infer<typeof kgInsightAlertPayloadSchema>;
}

export interface AlertsResponsePayload {
  id: string;
  source_id: string;
  source_type: AlertSourceType;
  metadata?:
    | InsightAlertMetadata
    | AlfredAlertMetadata
    | KgInsightAlertMetadata;
  event: {
    type: 'alert';
    action: 'alert.scenario-insight' | 'alert.alfred' | 'alert.kg-insight';
  };
}

export type NotificationSocketResponses =
  | AlfredResponsePayload
  | PortfolioNotificationPayload
  | AlertsResponsePayload;
export type NotificationResponseTypes =
  NotificationSocketResponses['event']['type'];

export type FindByType<Union, T> = Union extends { event: { type: T } }
  ? Union
  : never;

export type NotificationResponseByType<T extends NotificationResponseTypes> =
  FindByType<NotificationSocketResponses, T>;

export type PortfolioNotificationPayload = {
  title: string;
  body: string;
  event: {
    action: 'connection.success' | 'connection.error';
    type: 'portfolio';
  };
  metadata?: {
    portfolio_id: string;
    provider_id: string;
    provider_name: string;
  };
};
